import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Trash2, Send, RotateCcw, Shield, Eye, UserX, UserCheck, KeyRound, Plus, X } from "lucide-react";
import { cmdApi } from "../api";
import { useCmdAuth } from "../contexts/CmdAuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { fmtDateTime } from "../format";

const MODULES = ["portfolio", "board", "decisions", "financials", "reports", "review"];
const LEVELS = ["none", "view", "edit"];
const VERTICALS_FALLBACK = ["Coffee", "Dairy", "Hospitality", "Retail food", "Sri Lanka", "Technology"];
const DEFAULT_ROLES = ["Founder", "Co-founder", "CFO", "Finance Director", "Vertical lead", "Investor", "Staff", "Unassigned"];
const PREVIEW_KEY = "cmd_preview";

function Sheet({ onClose, title, children, testId, wide }) {
  return (
    <>
      <div className="cmd-sheet-backdrop" onClick={onClose} />
      <div className="cmd-sheet" style={wide ? { width: "min(760px, 94vw)" } : undefined}
        role="dialog" aria-modal="true" data-testid={testId}>
        <div className="cmd-sheet-head">
          <h2>{title}</h2>
          <button className="cmd-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="cmd-sheet-body">{children}</div>
      </div>
    </>
  );
}

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

function VerticalChips({ value, onChange, disabled, verticals }) {
  const list = verticals && verticals.length ? verticals : VERTICALS_FALLBACK;
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div>
      <div className="cmd-vchips">
        {list.map((v) => (
          <button key={v} type="button" disabled={disabled}
            className={`cmd-vchip ${value.includes(v) ? "sel" : ""}`}
            onClick={() => toggle(v)} data-testid={`vchip-${v.replace(/\s/g, "-")}`}>{v}</button>
        ))}
      </div>
      <div className="cmd-muted" style={{ fontSize: 12, marginTop: 4 }}>
        {value.length === 0 ? "Sees all verticals" : `Sees only ${value.join(", ")}`}
      </div>
    </div>
  );
}

function PeopleTab({ users, roles, reload, selfId }) {
  const [editing, setEditing] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const save = async () => {
    try {
      await cmdApi.patch(`/access/users/${editing.id}`, {
        role: editing.role, verticals: editing.verticals, status: editing.status,
      });
      toast.success("User updated");
      setEditing(null); reload();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not update."); }
  };
  const toggleStatus = async (u) => {
    try {
      await cmdApi.patch(`/access/users/${u.id}`, { status: u.status === "disabled" ? "active" : "disabled" });
      toast.success(u.status === "disabled" ? "User re-enabled" : "User disabled — their session ends on next request");
      reload();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not change status."); }
  };
  const remove = async (u) => {
    try { await cmdApi.delete(`/access/users/${u.id}`); toast.success("User removed"); reload(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Could not remove."); }
  };
  const resetPw = async () => {
    try {
      await cmdApi.post(`/access/users/${pwUser.id}/password`, { password: pwUser.password });
      toast.success("Password reset — share it with the user");
      setPwUser(null);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not reset."); }
  };

  return (
    <>
      <div className="cmd-pl-wrap">
        <table className="cmd-pl">
          <thead><tr><th>Person</th><th>Role</th><th>Verticals</th><th>Status</th><th>Last sign-in</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div className="cmd-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{initials(u.name)}</div>
                    <div><div style={{ fontWeight: 600 }}>{u.name}</div><div className="cmd-muted" style={{ fontSize: 12 }}>{u.email}</div></div>
                  </div>
                </td>
                <td>{u.role}{u.role === "Founder" && <span className="cmd-chip" style={{ marginLeft: 6 }}><Shield size={10} /> Locked</span>}</td>
                <td>{u.verticals.length === 0 ? <span className="cmd-muted">All</span> : u.verticals.join(", ")}</td>
                <td>
                  <span className={`cmd-pill ${u.status === "active" ? "health-ok" : u.status === "invited" ? "stage-building" : ""}`}>
                    {u.status}
                  </span>
                </td>
                <td>{u.last_login_at ? fmtDateTime(u.last_login_at) : <span className="cmd-muted">Never</span>}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {u.role !== "Founder" && (
                    <>
                      <button className="cmd-link" style={{ width: "auto" }}
                        onClick={() => setEditing({ ...u, verticals: [...u.verticals] })} data-testid={`user-edit-${u.id}`}>Edit</button>
                      <button className="cmd-link" style={{ width: "auto" }}
                        onClick={() => toggleStatus(u)} data-testid={`user-toggle-${u.id}`}>
                        {u.status === "disabled" ? <UserCheck size={13} /> : <UserX size={13} />}
                      </button>
                      <button className="cmd-link" style={{ width: "auto" }}
                        onClick={() => setPwUser({ ...u, password: "" })} data-testid={`user-pw-${u.id}`}>
                        <KeyRound size={13} />
                      </button>
                      {u.id !== selfId && (
                        <button className="cmd-link" style={{ width: "auto", color: "var(--cmd-health-risk)" }}
                          onClick={() => remove(u)} data-testid={`user-remove-${u.id}`}><Trash2 size={13} /></button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Sheet onClose={() => setEditing(null)} title={`Edit ${editing.name}`} testId="user-edit-sheet">
          <div className="cmd-field"><label>Role</label>
            <select className="cmd-input" value={editing.role} onChange={(e) => setEditing((s) => ({ ...s, role: e.target.value }))}
              data-testid="user-edit-role">
              {roles.filter((r) => r !== "Founder").map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="cmd-field"><label>Verticals</label>
            <VerticalChips value={editing.verticals} onChange={(v) => setEditing((s) => ({ ...s, verticals: v }))} />
          </div>
          <div className="cmd-field"><label>Status</label>
            <select className="cmd-input" value={editing.status} onChange={(e) => setEditing((s) => ({ ...s, status: e.target.value }))}>
              <option value="active">Active</option><option value="disabled">Disabled</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="cmd-btn cmd-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button className="cmd-btn cmd-btn-primary" onClick={save} data-testid="user-edit-save">Save</button>
          </div>
        </Sheet>
      )}
      {pwUser && (
        <Sheet onClose={() => setPwUser(null)} title={`Reset password · ${pwUser.name}`} testId="user-pw-sheet">
          <div className="cmd-field"><label>New password (min 8)</label>
            <input className="cmd-input" type="text" value={pwUser.password}
              onChange={(e) => setPwUser((s) => ({ ...s, password: e.target.value }))} data-testid="user-pw-new" />
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="cmd-btn cmd-btn-ghost" onClick={() => setPwUser(null)}>Cancel</button>
            <button className="cmd-btn cmd-btn-primary" onClick={resetPw} disabled={(pwUser.password || "").length < 8}
              data-testid="user-pw-save">Reset password</button>
          </div>
        </Sheet>
      )}
    </>
  );
}

function InvitesTab({ invites, roles, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "Investor", verticals: [] });
  const [lastLink, setLastLink] = useState(null);
  const create = async () => {
    try {
      const { data } = await cmdApi.post("/access/invites", f);
      const url = `${window.location.origin}${data.accept_url}`;
      setLastLink(url);
      toast.success("Invite created — copy the link below");
      setF({ name: "", email: "", role: "Investor", verticals: [] });
      reload();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not invite."); }
  };
  const resend = async (t) => {
    try { await cmdApi.post(`/access/invites/${t}/resend`); toast.success("Invite extended by 7 days"); reload(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Could not resend."); }
  };
  const revoke = async (t) => {
    try { await cmdApi.delete(`/access/invites/${t}`); toast.success("Invite revoked"); reload(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Could not revoke."); }
  };
  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Link copied"); };

  return (
    <>
      <div className="cmd-toolbar"><button className="cmd-btn cmd-btn-primary" onClick={() => setOpen(true)} data-testid="invite-new">
        <Plus size={14} style={{ marginRight: 5 }} />Invite someone
      </button></div>
      {invites.length === 0 ? (
        <div className="cmd-empty" data-testid="invites-empty"><h3>No pending invites</h3><p>Invite someone to see them appear here.</p></div>
      ) : (
        <div className="cmd-pl-wrap">
          <table className="cmd-pl">
            <thead><tr><th>Email</th><th>Role</th><th>Verticals</th><th>Expires</th><th>Link</th><th></th></tr></thead>
            <tbody>
              {invites.map((i) => {
                const url = `${window.location.origin}${i.accept_url}`;
                return (
                  <tr key={i.token} data-testid={`invite-row-${i.token}`}>
                    <td>{i.email}<div className="cmd-muted" style={{ fontSize: 12 }}>{i.name}</div></td>
                    <td>{i.role}</td>
                    <td>{(i.verticals || []).join(", ") || <span className="cmd-muted">All</span>}</td>
                    <td>{fmtDateTime(i.expires_at)}</td>
                    <td><button className="cmd-link" style={{ width: "auto" }} onClick={() => copy(url)} data-testid={`invite-copy-${i.token}`}><Copy size={12} /> Copy</button></td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="cmd-link" style={{ width: "auto" }} onClick={() => resend(i.token)} data-testid={`invite-resend-${i.token}`}><Send size={12} /></button>
                      <button className="cmd-link" style={{ width: "auto", color: "var(--cmd-health-risk)" }} onClick={() => revoke(i.token)} data-testid={`invite-revoke-${i.token}`}><Trash2 size={12} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {lastLink && (
        <div className="cmd-fig-strip" style={{ marginTop: 14 }} data-testid="last-invite-link">
          <div><div className="f">Share this link</div><div className="v" style={{ fontSize: 13, wordBreak: "break-all" }}>{lastLink}</div></div>
          <button className="cmd-btn" onClick={() => copy(lastLink)}><Copy size={13} style={{ marginRight: 5 }} />Copy</button>
        </div>
      )}
      {open && (
        <Sheet onClose={() => setOpen(false)} title="Invite someone" testId="invite-sheet">
          <div className="cmd-field"><label>Name</label><input className="cmd-input" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="invite-name" /></div>
          <div className="cmd-field"><label>Email</label><input className="cmd-input" type="email" value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} data-testid="invite-email" /></div>
          <div className="cmd-field"><label>Role</label>
            <select className="cmd-input" value={f.role} onChange={(e) => setF((s) => ({ ...s, role: e.target.value }))} data-testid="invite-role">
              {roles.filter((r) => r !== "Founder").map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="cmd-field"><label>Verticals</label><VerticalChips value={f.verticals} onChange={(v) => setF((s) => ({ ...s, verticals: v }))} /></div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="cmd-btn cmd-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="cmd-btn cmd-btn-primary" onClick={async () => { await create(); setOpen(false); }} data-testid="invite-save">Send invite</button>
          </div>
        </Sheet>
      )}
    </>
  );
}

function MatrixTab({ roles, matrix, closerRoles, reload }) {
  const [draft, setDraft] = useState({ matrix, closer: closerRoles });
  const [dirty, setDirty] = useState(false);
  const [newRole, setNewRole] = useState("");
  useEffect(() => { setDraft({ matrix, closer: closerRoles }); setDirty(false); }, [matrix, closerRoles]);

  const set = (role, mod, lvl) => {
    setDraft((s) => ({ ...s, matrix: { ...s.matrix, [role]: { ...s.matrix[role], [mod]: lvl } } }));
    setDirty(true);
  };
  const toggleCloser = (role) => {
    const has = draft.closer.includes(role);
    setDraft((s) => ({ ...s, closer: has ? s.closer.filter((r) => r !== role) : [...s.closer, role] }));
    setDirty(true);
  };
  const save = async () => {
    try {
      await cmdApi.put("/roles-config", { matrix: draft.matrix });
      await cmdApi.put("/access/closer-roles", { roles: draft.closer });
      toast.success("Access saved — applies to everyone's next request");
      setDirty(false); reload();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not save."); }
  };
  const addRole = async () => {
    if (!newRole.trim()) return;
    try { await cmdApi.post("/access/roles", { name: newRole.trim() }); toast.success(`Added ${newRole}`); setNewRole(""); reload(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Could not add."); }
  };
  const removeRole = async (name) => {
    if (!window.confirm(`Remove role "${name}"? Any user with this role will become Unassigned.`)) return;
    try { const { data } = await cmdApi.delete(`/access/roles/${encodeURIComponent(name)}`); toast.success(`Removed · ${data.affected_users} user(s) moved to Unassigned`); reload(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Could not remove."); }
  };

  return (
    <>
      <div className="cmd-pl-wrap">
        <table className="cmd-pl">
          <thead><tr><th>Role</th>{MODULES.map((m) => <th key={m}>{m[0].toUpperCase() + m.slice(1)}</th>)}<th>Close decisions</th><th></th></tr></thead>
          <tbody>
            {roles.map((role) => {
              const perms = draft.matrix[role] || {};
              const isFounder = role === "Founder";
              return (
                <tr key={role} data-testid={`matrix-row-${role.replace(/\s/g, "-")}`}>
                  <td>{role}{isFounder && <span className="cmd-chip" style={{ marginLeft: 6 }}>Locked</span>}</td>
                  {MODULES.map((m) => (
                    <td key={m}>
                      <select className="cmd-input" style={{ width: 100, padding: "4px 8px", fontSize: 13 }}
                        value={perms[m] || "none"} disabled={isFounder}
                        onChange={(e) => set(role, m, e.target.value)}
                        data-testid={`matrix-${role.replace(/\s/g, "-")}-${m}`}>
                        {LEVELS.map((l) => <option key={l} value={l}>{l === "none" ? "No access" : l === "view" ? "View" : "Edit"}</option>)}
                      </select>
                    </td>
                  ))}
                  <td style={{ textAlign: "left" }}>
                    <label style={{ fontSize: 13 }}><input type="checkbox" checked={draft.closer.includes(role)} disabled={isFounder}
                      onChange={() => toggleCloser(role)} data-testid={`closer-${role.replace(/\s/g, "-")}`} /> Allowed</label>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {!DEFAULT_ROLES.includes(role) && (
                      <button className="cmd-link" style={{ width: "auto", color: "var(--cmd-health-risk)" }}
                        onClick={() => removeRole(role)} data-testid={`matrix-remove-${role}`}>Remove</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="cmd-toolbar" style={{ marginTop: 14 }}>
        <input className="cmd-input" style={{ maxWidth: 240 }} placeholder="New role name (e.g. Head chef)"
          value={newRole} onChange={(e) => setNewRole(e.target.value)} data-testid="role-add-name" />
        <button className="cmd-btn" onClick={addRole} data-testid="role-add"><Plus size={14} style={{ marginRight: 5 }} />Add custom role</button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
          <button className="cmd-btn cmd-btn-ghost" disabled={!dirty} onClick={() => { setDraft({ matrix, closer: closerRoles }); setDirty(false); }} data-testid="matrix-discard">Discard changes</button>
          <button className="cmd-btn cmd-btn-primary" disabled={!dirty} onClick={save} data-testid="matrix-save">Save access</button>
        </div>
      </div>
    </>
  );
}

function AuditTab() {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ entity: "", action: "", user_id: "" });
  const [opened, setOpened] = useState({});
  const [users, setUsers] = useState([]);
  const load = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      params.set("page_size", "100");
      const [{ data }, { data: us }] = await Promise.all([
        cmdApi.get(`/audit?${params}`), cmdApi.get("/access/users"),
      ]);
      setRows(data.items); setUsers(us.items);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not load audit."); setRows([]); }
  };
  useEffect(() => { load(); }, [filters]); // eslint-disable-line

  return (
    <>
      <div className="cmd-toolbar">
        <input className="cmd-input cmd-filter" placeholder="Entity (e.g. initiative)" value={filters.entity}
          onChange={(e) => setFilters((s) => ({ ...s, entity: e.target.value }))} data-testid="audit-entity" />
        <input className="cmd-input cmd-filter" placeholder="Action (e.g. publish)" value={filters.action}
          onChange={(e) => setFilters((s) => ({ ...s, action: e.target.value }))} data-testid="audit-action" />
        <select className="cmd-input cmd-filter" value={filters.user_id} onChange={(e) => setFilters((s) => ({ ...s, user_id: e.target.value }))} data-testid="audit-user">
          <option value="">All users</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      {rows === null ? <p className="cmd-muted">Loading…</p>
        : rows.length === 0 ? <div className="cmd-empty"><h3>No audit rows match</h3></div>
          : (
            <div className="cmd-pl-wrap">
              <table className="cmd-pl">
                <thead><tr><th>When</th><th>Who</th><th>Entity</th><th>Action</th><th>Entity id</th><th></th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <>
                      <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                        <td>{fmtDateTime(r.at)}</td>
                        <td>{r.user_name || <span className="cmd-muted">system</span>}<div className="cmd-muted" style={{ fontSize: 12 }}>{r.user_role}</div></td>
                        <td>{r.entity}</td>
                        <td>{r.action}</td>
                        <td style={{ fontSize: 12, color: "var(--cmd-muted)", wordBreak: "break-all" }}>{r.entity_id}</td>
                        <td style={{ textAlign: "right" }}>
                          <button className="cmd-link" style={{ width: "auto" }} onClick={() => setOpened((s) => ({ ...s, [r.id]: !s[r.id] }))} data-testid={`audit-toggle-${r.id}`}>
                            {opened[r.id] ? "Hide" : "Diff"}
                          </button>
                        </td>
                      </tr>
                      {opened[r.id] && (
                        <tr><td colSpan={6} style={{ background: "var(--cmd-surface)" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12 }}>
                            <div><div className="cmd-muted">Before</div><pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(r.before, null, 2)}</pre></div>
                            <div><div className="cmd-muted">After</div><pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(r.after, null, 2)}</pre></div>
                          </div>
                        </td></tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </>
  );
}

function PreviewAsBar() {
  const [preview, setPreview] = useState(null);
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    const raw = sessionStorage.getItem(PREVIEW_KEY);
    if (raw) setPreview(JSON.parse(raw));
    cmdApi.get("/roles-config").then(({ data }) => setRoles(data.roles || [])).catch(() => {});
  }, []);
  const set = (role, verticals) => {
    const next = { role, verticals };
    sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(next));
    setPreview(next);
    cmdApi.defaults.headers["X-Preview-As"] = role;
    cmdApi.defaults.headers["X-Preview-Verticals"] = (verticals || []).join(",");
    toast.success(`Previewing as ${role}`);
    navigate("/cmd");
  };
  const exit = () => {
    sessionStorage.removeItem(PREVIEW_KEY);
    delete cmdApi.defaults.headers["X-Preview-As"];
    delete cmdApi.defaults.headers["X-Preview-Verticals"];
    setPreview(null);
    window.location.href = "/cmd/access";
  };
  if (preview) return null;  // the banner (in CmdShell) handles exit while preview is active
  return (
    <div className="cmd-fig-strip" style={{ marginBottom: 16 }} data-testid="preview-as">
      <div><div className="f">Preview as</div>
        <select className="cmd-input" style={{ marginTop: 4, width: 220 }}
          onChange={(e) => e.target.value && set(e.target.value, [])} defaultValue="" data-testid="preview-role">
          <option value="">Pick a role…</option>
          {roles.filter((r) => r !== "Founder").map((r) => <option key={r}>{r}</option>)}
        </select>
      </div>
      <div style={{ flex: 1 }}><div className="f">Verticals (optional, pick to narrow)</div>
        <div className="cmd-vchips" style={{ marginTop: 4 }}>
          {VERTICALS_FALLBACK.map((v) => (
            <button key={v} type="button" className="cmd-vchip"
              onClick={() => {
                const cur = JSON.parse(sessionStorage.getItem(PREVIEW_KEY) || "null");
                const base = cur || { role: null, verticals: [] };
                if (!base.role) { toast.error("Pick a role first."); return; }
                set(base.role, base.verticals.includes(v) ? base.verticals.filter((x) => x !== v) : [...base.verticals, v]);
              }} data-testid={`preview-vertical-${v.replace(/\s/g, "-")}`}>{v}</button>
          ))}
        </div>
      </div>
      <button className="cmd-btn cmd-btn-ghost" onClick={exit}>Clear</button>
    </div>
  );
}

export default function CmdAccess() {
  const { me } = useCmdAuth();
  const { isFounder } = usePermissions();
  const [tab, setTab] = useState("people");
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [closerRoles, setCloserRoles] = useState([]);
  const [roles, setRoles] = useState([]);

  const reload = async () => {
    try {
      const [u, i, cfg, cr] = await Promise.all([
        cmdApi.get("/access/users"),
        cmdApi.get("/access/invites"),
        cmdApi.get("/roles-config"),
        cmdApi.get("/access/closer-roles"),
      ]);
      setUsers(u.data.items); setInvites(i.data.items);
      setRoles(cfg.data.roles || []); setMatrix(cfg.data.matrix || {});
      setCloserRoles(cr.data.roles || []);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not load Access."); }
  };
  useEffect(() => { reload(); }, []);

  if (!isFounder) return <Navigate to="/cmd" replace />;

  return (
    <section data-testid="cmd-access">
      <PreviewAsBar />
      <div className="cmd-view-toggle" style={{ marginBottom: 16 }}>
        {[["people", "People"], ["invites", "Invites"], ["matrix", "Permissions"], ["audit", "Audit log"]].map(([k, l]) => (
          <button key={k} className={`cmd-view-btn ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} data-testid={`access-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "people" && <PeopleTab users={users} roles={roles} reload={reload} selfId={me?.user?.id} />}
      {tab === "invites" && <InvitesTab invites={invites} roles={roles} reload={reload} />}
      {tab === "matrix" && <MatrixTab roles={roles} matrix={matrix} closerRoles={closerRoles} reload={reload} />}
      {tab === "audit" && <AuditTab />}
    </section>
  );
}
